import React, { useState, useContext, useEffect } from 'react';
import {
  Typography, Upload, Button, Alert, Descriptions, Tag, Space, Spin,
  message, Table, Select, Popconfirm, Segmented,
} from 'antd';
import {
  InboxOutlined, UploadOutlined, SaveOutlined,
  CloudUploadOutlined, TableOutlined,
} from '@ant-design/icons';
import api from '../api';
import { UserContext } from '../App';

const { Dragger } = Upload;
const { Title, Text } = Typography;

export default function ConHomeUpsellImportPage() {
  const currentUser = useContext(UserContext);
  const isAdmin = currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin'
    || (currentUser?.JobID === 6 && currentUser?.TeamID === 1);

  const [view, setView]               = useState('upload');
  const [file, setFile]               = useState(null);
  const [loading, setLoading]         = useState(false);
  const [result, setResult]           = useState(null);
  const [error, setError]             = useState(null);
  const [allUsers, setAllUsers]       = useState([]);
  const [assignments, setAssignments] = useState({});  // owner -> user_id
  const [saving, setSaving]           = useState(false);

  // ── Browse imported transactions ───────────────────────────────────────────
  const [months, setMonths]                 = useState([]);
  const [monthsLoading, setMonthsLoading]   = useState(false);
  const [browseMonth, setBrowseMonth]       = useState(null);
  const [txns, setTxns]                     = useState([]);
  const [txnsLoading, setTxnsLoading]       = useState(false);
  const [assigningOwner, setAssigningOwner] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    api.get('/api/admin/conhome-upsell-users')
      .then(r => setAllUsers(r.data || []))
      .catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin || view !== 'browse') return;
    setMonthsLoading(true);
    api.get('/api/admin/conhome-upsell-months')
      .then(r => {
        const list = r.data || [];
        setMonths(list);
        setBrowseMonth(prev => prev ?? (list.length ? list[0] : null));
      })
      .catch(() => setMonths([]))
      .finally(() => setMonthsLoading(false));
  }, [isAdmin, view]);

  useEffect(() => {
    if (!isAdmin || view !== 'browse' || !browseMonth) { setTxns([]); return; }
    setTxnsLoading(true);
    api.get('/api/admin/conhome-upsell-transactions', { params: { month: browseMonth } })
      .then(r => setTxns(r.data || []))
      .catch(() => setTxns([]))
      .finally(() => setTxnsLoading(false));
  }, [isAdmin, view, browseMonth]);

  if (!isAdmin) {
    return <div style={{ padding: 24 }}><Text type="secondary">Access denied.</Text></div>;
  }

  const handleUpload = async () => {
    if (!file) { message.warning('Please select a file first.'); return; }
    setLoading(true);
    setResult(null);
    setError(null);
    setAssignments({});
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await api.post('/api/admin/conhome-upsell-import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      setFile(null);
      if ((res.data.unresolved || []).length === 0) {
        message.success('Import complete.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAssignments = async () => {
    const payload = Object.entries(assignments)
      .filter(([, uid]) => uid)
      .map(([owner, user_id]) => ({ owner, user_id }));
    if (!payload.length) { message.warning('No assignments to save.'); return; }
    setSaving(true);
    try {
      await api.patch('/api/admin/conhome-upsell-assignments', payload);
      message.success('Assignments saved.');
      // Remove now-resolved owners from unresolved list
      const resolved = new Set(payload.map(p => p.owner));
      setResult(prev => ({
        ...prev,
        unresolved: (prev.unresolved || []).filter(o => !resolved.has(o)),
      }));
      setAssignments({});
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save assignments.');
    } finally {
      setSaving(false);
    }
  };

  const userOptions = allUsers.map(u => ({
    value: u.ID,
    label: u.IsActive ? (u.DisplayName || u.Email) : `${u.DisplayName || u.Email} (inactive)`,
  }));

  const unresolvedOwners = result?.unresolved || [];

  const assignColumns = [
    { title: 'Owner (from file)', dataIndex: 'owner', key: 'owner' },
    {
      title: 'Assign to User',
      key: 'user',
      render: (_, row) => (
        <Select
          showSearch
          placeholder="Select user..."
          style={{ minWidth: 260 }}
          value={assignments[row.owner] || undefined}
          onChange={uid => setAssignments(prev => ({ ...prev, [row.owner]: uid }))}
          filterOption={(input, opt) => (opt?.label || '').toLowerCase().includes(input.toLowerCase())}
          options={userOptions}
          size="small"
        />
      ),
    },
  ];

  const unassignedCount = txns.filter(t => !t.UserName).length;

  // Fix an unassigned row from the View Imported tab. Assignment is by owner
  // name, not by row, because that is the key the importer matches on -- so
  // this clears every unassigned row for that owner at once, and the next
  // import auto-resolves them (it builds its owner map from rows that already
  // carry a UserID).
  const assignOwner = async (owner, userId) => {
    if (!owner || !userId) return;
    setAssigningOwner(owner);
    try {
      await api.patch('/api/admin/conhome-upsell-assignments', [{ owner, user_id: userId }]);
      const r = await api.get('/api/admin/conhome-upsell-transactions', { params: { month: browseMonth } });
      const rows = r.data || [];
      setTxns(rows);
      message.success(`Assigned ${owner}.`);
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to assign.');
    } finally {
      setAssigningOwner(null);
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: view === 'browse' ? '100%' : 700 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 16, gap: 16, flexWrap: 'wrap',
      }}>
        <Title level={4} style={{ margin: 0 }}>ConHome Upsell</Title>
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { label: 'Import',        value: 'upload', icon: <CloudUploadOutlined /> },
            { label: 'View Imported', value: 'browse', icon: <TableOutlined /> },
          ]}
        />
      </div>

      {view === 'upload' && (
      <>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Upload a TSV export from Dynamics 365. Each row is inserted or updated using the
        Opportunity GUID as the unique key. The Premise column is not stored.
        CampaignID is set to 1 for all records.
      </Text>

      <Dragger
        accept=".tsv,.txt,.csv"
        beforeUpload={(f) => { setFile(f); setResult(null); setError(null); return false; }}
        fileList={file ? [file] : []}
        onRemove={() => setFile(null)}
        maxCount={1}
        style={{ marginBottom: 16 }}
      >
        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
        <p className="ant-upload-text">Click or drag a TSV file here</p>
        <p className="ant-upload-hint">Tab-separated export from Dynamics 365 (.tsv or .txt)</p>
      </Dragger>

      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<UploadOutlined />}
          loading={loading}
          onClick={handleUpload}
          disabled={!file}
        >
          Import
        </Button>
      </Space>

      {error && (
        <Alert type="error" message={error} style={{ marginTop: 16 }} showIcon />
      )}

      {result && (
        <Alert
          type={unresolvedOwners.length ? 'warning' : 'success'}
          style={{ marginTop: 16 }}
          message={unresolvedOwners.length ? 'Import complete — user assignments needed' : 'Import complete'}
          description={
            <Descriptions size="small" column={3} style={{ marginTop: 8 }}>
              <Descriptions.Item label="Inserted"><Tag color="green">{result.inserted}</Tag></Descriptions.Item>
              <Descriptions.Item label="Updated"><Tag color="blue">{result.updated}</Tag></Descriptions.Item>
              <Descriptions.Item label="Total">{result.total}</Descriptions.Item>
            </Descriptions>
          }
          showIcon
        />
      )}

      {unresolvedOwners.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            {unresolvedOwners.length} owner{unresolvedOwners.length !== 1 ? 's' : ''} could not be matched automatically.
            Select the corresponding QA user for each:
          </Text>
          <Table
            rowKey="owner"
            size="small"
            pagination={false}
            dataSource={unresolvedOwners.map(o => ({ owner: o }))}
            columns={assignColumns}
            style={{ marginBottom: 12 }}
          />
          <Popconfirm
            title="Save assignments"
            description="Confirm you are ready to save the selected user assignments."
            onConfirm={handleSaveAssignments}
            okText="Save"
            cancelText="Cancel"
            disabled={!Object.values(assignments).some(Boolean)}
          >
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={!Object.values(assignments).some(Boolean)}
            >
              Save Assignments
            </Button>
          </Popconfirm>
        </div>
      )}
      </>
      )}

      {/* ── VIEW IMPORTED ─────────────────────────────────────────────────── */}
      {view === 'browse' && (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <Text strong>Created Month:</Text>
            {monthsLoading ? <Spin size="small" /> : (
              <Select
                style={{ minWidth: 160 }}
                placeholder="Select month"
                value={browseMonth}
                onChange={setBrowseMonth}
                options={months.map(m => ({ label: m, value: m }))}
                showSearch
                notFoundContent="Nothing imported yet"
              />
            )}
            {txns.length > 0 && (
              <>
                <Text type="secondary">{txns.length} opportunities</Text>
                {unassignedCount > 0 && <Tag color="orange">{unassignedCount} unassigned</Tag>}
              </>
            )}
          </div>

          {txnsLoading ? <Spin /> : !browseMonth ? (
            <Text type="secondary">Select a month to view imported opportunities.</Text>
          ) : txns.length === 0 ? (
            <Alert type="info" showIcon message={`No opportunities created in ${browseMonth}.`} />
          ) : (
            <Table
              rowKey="OpportunityID"
              dataSource={txns}
              size="small"
              pagination={false}
              bordered
              scroll={{ x: 'max-content', y: 600 }}
              columns={[
                {
                  title: 'Owner (from file)',
                  dataIndex: 'Owner',
                  key: 'Owner',
                  width: 180,
                  render: v => v || <Text type="secondary">—</Text>,
                },
                {
                  title: 'QA User',
                  key: 'user',
                  width: 250,
                  render: (_, r) => {
                    if (r.UserName) {
                      return (
                        <span>
                          {r.UserName}
                          {!r.UserIsActive && <Tag color="default" style={{ marginLeft: 6 }}>Inactive</Tag>}
                        </span>
                      );
                    }
                    // Assignment keys off the owner name, so a row with no
                    // owner cannot be fixed here -- say so rather than showing
                    // a dropdown that would silently do nothing.
                    if (!r.Owner) return <Tag color="orange">Unassigned (no owner)</Tag>;
                    return (
                      <Select
                        showSearch
                        size="small"
                        style={{ width: '100%' }}
                        placeholder="Unassigned - pick a user"
                        value={undefined}
                        loading={assigningOwner === r.Owner}
                        disabled={assigningOwner != null}
                        options={userOptions}
                        filterOption={(input, opt) => (opt?.label || '').toLowerCase().includes(input.toLowerCase())}
                        onChange={uid => assignOwner(r.Owner, uid)}
                        status="warning"
                      />
                    );
                  },
                },
                {
                  title: 'Topic',
                  dataIndex: 'Topic',
                  key: 'Topic',
                  width: 220,
                  ellipsis: true,
                  render: v => v || <Text type="secondary">—</Text>,
                },
                {
                  title: 'Potential Customer',
                  dataIndex: 'PotentialCustomer',
                  key: 'PotentialCustomer',
                  width: 200,
                  ellipsis: true,
                  render: v => v || <Text type="secondary">—</Text>,
                },
                {
                  title: 'Product',
                  dataIndex: 'Product',
                  key: 'Product',
                  width: 150,
                  render: v => v || <Text type="secondary">—</Text>,
                },
                {
                  title: 'Source',
                  dataIndex: 'Source',
                  key: 'Source',
                  width: 130,
                  render: v => v || <Text type="secondary">—</Text>,
                },
                {
                  title: 'Created On',
                  dataIndex: 'CreatedOn',
                  key: 'CreatedOn',
                  width: 160,
                  render: v => v || <Text type="secondary">—</Text>,
                },
                {
                  title: 'Imported',
                  dataIndex: 'ImportedAt',
                  key: 'ImportedAt',
                  width: 150,
                  render: v => v ? String(v).replace('T', ' ').substring(0, 16) : '—',
                },
              ]}
            />
          )}
        </>
      )}
    </div>
  );
}
